// Sync history storage operations

import { 
    STORES, 
    openDatabase, 
    getAllFromStore, 
    getFromStore, 
    putToStore, 
    clearStore,
    countStore
} from './db-core';

interface SyncLogEntry {
    timestamp: string;
    level: number;
    message: string;
    details?: string;
    entityType?: string;
    entityId?: string;
}

interface SyncEvent {
    id: string;
    startedAt: string;
    completedAt?: string;
    success: boolean;
    reportsSynced: number;
    reportsFailed: number;
    photosUploaded: number;
    photosFailed: number;
    bytesUploaded: number;
    logEntries: SyncLogEntry[];
}

export const syncHistoryStorage = {
    async getSyncEvents(page: number, pageSize: number): Promise<string> {
        try {
            const allEvents = await getAllFromStore<SyncEvent>(STORES.SYNC_EVENTS);
            
            // Sort by startedAt descending (most recent first)
            allEvents.sort((a, b) => 
                new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
            );

            const totalCount = allEvents.length;
            const startIndex = (page - 1) * pageSize;
            const events = allEvents.slice(startIndex, startIndex + pageSize);

            return JSON.stringify({ events, totalCount });
        } catch (error) {
            console.error('[IndexedDB] getSyncEvents failed:', error);
            return JSON.stringify({ events: [], totalCount: 0 });
        }
    },

    async getSyncEvent(eventId: string): Promise<string | null> {
        try {
            const event = await getFromStore<SyncEvent>(STORES.SYNC_EVENTS, eventId);
            return event ? JSON.stringify(event) : null;
        } catch (error) {
            console.error('[IndexedDB] getSyncEvent failed:', error);
            return null;
        }
    },

    async saveSyncEvent(eventJson: string, maxEvents: number): Promise<boolean> {
        try {
            const event = JSON.parse(eventJson) as SyncEvent;
            await putToStore(STORES.SYNC_EVENTS, event);

            // Enforce maximum events limit
            await this.enforceMaxEvents(maxEvents);

            return true;
        } catch (error) {
            console.error('[IndexedDB] saveSyncEvent failed:', error);
            return false;
        }
    },

    async addSyncLogEntry(eventId: string, entryJson: string): Promise<boolean> {
        try {
            const event = await getFromStore<SyncEvent>(STORES.SYNC_EVENTS, eventId);
            if (!event) {
                return false;
            }

            const entry = JSON.parse(entryJson) as SyncLogEntry;
            event.logEntries.push(entry);
            await putToStore(STORES.SYNC_EVENTS, event);

            return true;
        } catch (error) {
            console.error('[IndexedDB] addSyncLogEntry failed:', error);
            return false;
        }
    },

    async clearSyncHistory(): Promise<boolean> {
        try {
            await clearStore(STORES.SYNC_EVENTS);
            return true;
        } catch (error) {
            console.error('[IndexedDB] clearSyncHistory failed:', error);
            return false;
        }
    },

    async enforceMaxEvents(maxEvents: number): Promise<void> {
        try {
            const count = await countStore(STORES.SYNC_EVENTS);
            
            if (count <= maxEvents) {
                return;
            }

            const allEvents = await getAllFromStore<SyncEvent>(STORES.SYNC_EVENTS);
            
            // Sort by startedAt ascending (oldest first)
            allEvents.sort((a, b) => 
                new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime()
            );

            // Delete oldest events to get back to maxEvents
            const eventsToDelete = allEvents.slice(0, count - maxEvents);
            const db = await openDatabase();
            
            for (const event of eventsToDelete) {
                const transaction = db.transaction(STORES.SYNC_EVENTS, 'readwrite');
                const store = transaction.objectStore(STORES.SYNC_EVENTS);
                store.delete(event.id);
            }

            console.log(`[IndexedDB] Removed ${eventsToDelete.length} old sync events`);
        } catch (error) {
            console.error('[IndexedDB] enforceMaxEvents failed:', error);
        }
    }
};
